import React, { useState, useEffect, useCallback } from 'react';
import { onboardingAPI } from '../../../services/api';
import { useNotifications } from '../../../context/NotificationContext';
import Spinner from '../../UIKit/Spinner';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tab,
  IconButton,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import Tabs from '../../UIKit/Tabs';
import Button from '../../UIKit/Button';
import Card from '../../UIKit/Card';
import ConfirmDialog from '../../ConfirmDialog';
import { EditOnboardingDialog } from './OnboardingEditor';
import { PreviewOnboardingDialog } from './OnboardingPreview';

const OnboardingManager = () => {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const { setError } = useNotifications();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [currentTab, setCurrentTab] = useState('guides');

  // Состояние для диалоговых окон
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [selectedGuideId, setSelectedGuideId] = useState(null);
  const [isNewGuide, setIsNewGuide] = useState(false);
  const [guideToDelete, setGuideToDelete] = useState(null);

  // Функция для мультиязычного вывода
  const lang = i18n.language || 'en';
  const getLocalized = (val) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    return val[lang] || val['en'] || val['ru'] || '';
  };

  // Загрузка списка гидов
  const fetchGuides = useCallback(async () => {
    try {
      setLoading(true);
      const response = await onboardingAPI.admin.getAllGuides();
      setGuides(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Ошибка при загрузке гидов:', error);
      setError && setError(t('admin:onboarding.loadError'));
      setLoading(false);
    }
  }, [t, setError]);

  // Загрузка аналитики
  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await onboardingAPI.admin.getAnalytics();
      setAnalytics(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Ошибка при загрузке аналитики:', error);
      setError && setError(t('admin:onboarding.loadError'));
      setLoading(false);
    }
  }, [t, setError]);

  // Открытие диалога подтверждения удаления
  const handleOpenDeleteDialog = (guide) => {
    setGuideToDelete(guide);
    setConfirmDeleteDialogOpen(true);
  };

  // Удаление гида
  const handleDeleteGuide = async () => {
    if (!guideToDelete) return;

    try {
      await onboardingAPI.admin.deleteGuide(guideToDelete._id);
      setConfirmDeleteDialogOpen(false);
      setGuideToDelete(null);
      fetchGuides();
    } catch (error) {
      console.error('Ошибка при удалении гида:', error);
      setError && setError(t('admin:onboarding.deleteError'));
    }
  };

  // Закрытие диалога подтверждения удаления
  const handleCloseDeleteDialog = () => {
    setConfirmDeleteDialogOpen(false);
    setGuideToDelete(null);
  };

  // Обновление статуса гида (активный/неактивный)
  const handleToggleGuideStatus = async (guide) => {
    try {
      await onboardingAPI.admin.updateGuide(guide._id, {
        ...guide,
        active: !guide.active,
      });
      fetchGuides();
    } catch (error) {
      console.error('Ошибка при обновлении статуса гида:', error);
      setError && setError(t('admin:onboarding.updateError'));
    }
  };

  // Открытие диалога редактирования
  const handleOpenEditDialog = (guideId = null) => {
    setSelectedGuideId(guideId);
    setIsNewGuide(!guideId);
    setEditDialogOpen(true);
  };

  // Открытие диалога предпросмотра
  const handleOpenPreviewDialog = (guideId) => {
    setSelectedGuideId(guideId);
    setPreviewDialogOpen(true);
  };

  // Закрытие диалогов и обновление данных
  const handleCloseDialogs = () => {
    setEditDialogOpen(false);
    setPreviewDialogOpen(false);
    fetchGuides();
  };

  // Загружаем данные при монтировании
  useEffect(() => {
    fetchGuides();
  }, [fetchGuides]);

  // Загружаем аналитику при переключении на вкладку аналитики
  useEffect(() => {
    if (currentTab === 'analytics') {
      fetchAnalytics();
    }
  }, [currentTab, fetchAnalytics]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Рендер списка гидов
  const renderGuidesList = () => {
    if (guides.length === 0 && !loading) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" py={8}>
          <Typography variant="h6" color="textSecondary" mb={3}>
            {t('admin:onboarding.noGuides')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenEditDialog()}
          >
            {t('admin:onboarding.createGuide')}
          </Button>
        </Box>
      );
    }

    return (
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h5" component="h2">
            {t('admin:onboarding.guidesTitle')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenEditDialog()}
          >
            {t('admin:onboarding.createGuide')}
          </Button>
        </Box>

        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('admin:onboarding.name')}</TableCell>
                <TableCell align="center">
                  {t('admin:onboarding.steps')}
                </TableCell>
                <TableCell align="center">
                  {t('admin:onboarding.status')}
                </TableCell>
                <TableCell>{t('admin:onboarding.roles')}</TableCell>
                <TableCell align="center">
                  {t('admin:onboarding.autostart')}
                </TableCell>
                <TableCell>{t('admin:onboarding.pageUrl')}</TableCell>
                <TableCell>{t('admin:onboarding.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {guides.map((guide) => (
                <TableRow
                  key={guide._id}
                  sx={{ opacity: guide.active ? 1 : 0.7 }}
                >
                  <TableCell>
                    <Typography fontWeight="medium">
                      {getLocalized(guide.name)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {guide.steps?.length || 0}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={guide.active ? <ActiveIcon /> : <InactiveIcon />}
                      label={
                        guide.active
                          ? t('admin:onboarding.statusActive')
                          : t('admin:onboarding.statusInactive')
                      }
                      color={guide.active ? 'success' : 'default'}
                      variant="outlined"
                      onClick={() => handleToggleGuideStatus(guide)}
                      clickable
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {guide.roles?.map((role) => (
                        <Chip
                          key={role}
                          label={role}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={
                        guide.autostart
                          ? t('admin:onboarding.autostartYes')
                          : t('admin:onboarding.autostartNo')
                      }
                      color={guide.autostart ? 'info' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {guide.page?.url || '*'}{' '}
                    {guide.page?.tabId ? `(${guide.page.tabId})` : ''}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title={t('admin:onboarding.edit')}>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleOpenEditDialog(guide._id)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('admin:onboarding.delete')}>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleOpenDeleteDialog(guide)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('admin:onboarding.preview')}>
                        <IconButton
                          color="secondary"
                          size="small"
                          onClick={() => handleOpenPreviewDialog(guide._id)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Рендер аналитики
  const renderAnalytics = () => {
    if ((!analytics || !Array.isArray(analytics)) && !loading) {
      return (
        <Box display="flex" justifyContent="center" py={5}>
          <Typography variant="h6" color="textSecondary">
            {t('admin:onboarding.noAnalytics')}
          </Typography>
        </Box>
      );
    }

    if (!analytics || !Array.isArray(analytics)) return null;

    return (
      <Box>
        <Typography variant="h5" component="h2" mb={3}>
          {t('admin:onboarding.analyticsTitle')}
        </Typography>

        {/* Общая статистика по гидам */}
        <Box mb={5}>
          <Typography variant="h6" component="h3" mb={2}>
            {t('admin:onboarding.generalStats')}
          </Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin:onboarding.name')}</TableCell>
                  <TableCell>{t('admin:onboarding.version')}</TableCell>
                  <TableCell align="center">
                    {t('admin:onboarding.started')}
                  </TableCell>
                  <TableCell align="center">
                    {t('admin:onboarding.completed')}
                  </TableCell>
                  <TableCell align="center">
                    {t('admin:onboarding.completionRate')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics.map((stat) => (
                  <TableRow key={stat.guide._id}>
                    <TableCell>{getLocalized(stat.guide.name)}</TableCell>
                    <TableCell>{stat.guide.version}</TableCell>
                    <TableCell align="center">
                      {stat.stats.totalStarted}
                    </TableCell>
                    <TableCell align="center">{stat.stats.completed}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${stat.stats.completionRate.toFixed(0)}%`}
                        color={
                          stat.stats.completionRate > 70
                            ? 'success'
                            : stat.stats.completionRate > 40
                            ? 'warning'
                            : 'error'
                        }
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Статистика по завершённым гидам */}
        {analytics.filter((stat) => stat.stats.completed > 0).length > 0 && (
          <Box mb={5}>
            <Typography variant="h6" component="h3" mb={2}>
              {t('admin:onboarding.completionStats')}
            </Typography>
            <Grid container spacing={3}>
              {analytics
                .filter((stat) => stat.stats.completed > 0)
                .map((stat) => (
                  <Grid item xs={12} sm={6} md={4} key={stat.guide._id}>
                    <Card>
                      <Typography variant="h6" gutterBottom>
                        {getLocalized(stat.guide.name)}
                      </Typography>
                      <Box display="flex" justifyContent="space-between" mt={2}>
                        <Typography variant="body2" color="textSecondary">
                          {t('admin:onboarding.completedCount')}:
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {stat.stats.completed}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mt={1}>
                        <Typography variant="body2" color="textSecondary">
                          {t('admin:onboarding.avgCompletionTime')}:
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {stat.stats.avgCompletionTime}{' '}
                          {t('admin:onboarding.minutes')}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
            </Grid>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h5" component="h1" mb={2}>
        {t('admin:onboarding.title')}
      </Typography>

      <Box
        sx={{
          bgcolor: 'background.default',
          borderRadius: { xs: '12px', sm: '16px' },
          p: '4px',
          width: '100%',
          overflow: 'hidden',
          mb: 2,
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
        >
          <Tab
            value="guides"
            label={t('admin:onboarding.guidesTab')}
            id="tab-guides"
          />
          <Tab
            value="analytics"
            label={t('admin:onboarding.analyticsTab')}
            id="tab-analytics"
          />
        </Tabs>
      </Box>

      {loading ? (
        <Spinner />
      ) : (
        <Box>
          {currentTab === 'guides' && renderGuidesList()}
          {currentTab === 'analytics' && renderAnalytics()}
        </Box>
      )}

      {/* Диалоговые окна */}
      <EditOnboardingDialog
        open={editDialogOpen}
        onClose={handleCloseDialogs}
        guideId={selectedGuideId}
        isNew={isNewGuide}
      />

      <PreviewOnboardingDialog
        open={previewDialogOpen}
        onClose={handleCloseDialogs}
        guideId={selectedGuideId}
      />

      <ConfirmDialog
        open={confirmDeleteDialogOpen}
        onConfirm={handleDeleteGuide}
        onCancel={handleCloseDeleteDialog}
        title={t('admin:onboarding.confirmDeleteTitle')}
        content={guideToDelete ? t('admin:onboarding.confirmDeleteMessage', { 
          guideName: getLocalized(guideToDelete.name) 
        }) : ''}
        confirmText={t('admin:onboarding.delete')}
        cancelText={t('common:actions.cancel')}
      />
    </Box>
  );
};

export default OnboardingManager;
